/**
 * Fixed catalog of headline classification categories used by both the
 * per-headline pills and the filter chips. It maps each classifier string to
 * the display label the design calls for (ST-13) and fixes the chip order.
 *
 * Categories carry no per-category colour: the good/bad colour split was cut
 * (Tyler, Jun 4) in favour of one neutral treatment for every pill and chip.
 */
export interface HeadlineCategory {
  /** Exact classifier string, e.g. "M&A Deals". Filtering keys on this, never on the label. */
  name: string;
  /** What the user sees. Deliberately shorter than the classifier string for some entries. */
  label: string;
  /**
   * Abbreviated form for surfaces too narrow for `label` — the company page's Headlines
   * tab, whose column is a fraction of the full-width Headlines page. Formed by dropping
   * the generic noun and singularising, so it still reads as the category rather than as
   * a truncation. Equal to `label` where there is nothing left to cut, which is also how
   * callers decide whether a tooltip carrying the full name is worth showing.
   */
  shortLabel: string;
}

/**
 * Sentinel for "no classification". The classifier never emits it — a headline with no
 * classification simply carries an empty `categories` list — so it exists only so the two
 * spellings of unclassified can be normalised to one in a single place. It is no longer a chip:
 * hiding unclassified rows is the "Hide unclassified" checkbox on the headlines filter bar.
 */
export const UNCLASSIFIED_CATEGORY = 'None';

export const HEADLINE_CATEGORIES: Array<HeadlineCategory> = [
  { name: 'Earnings Reports', label: 'Earnings Reports', shortLabel: 'Earnings' },
  { name: 'Pharma Partnerships / Collabs', label: 'Partnerships', shortLabel: 'Partnerships' },
  { name: 'Regulatory Decisions', label: 'Regulatory Updates', shortLabel: 'Regulatory' },
  { name: 'Management Changes', label: 'Management Changes', shortLabel: 'Mgmt Change' },
  { name: 'M&A Deals', label: 'M&A Deals', shortLabel: 'M&A' },
  { name: 'Financings', label: 'Financings', shortLabel: 'Financing' },
  { name: 'Data Announcements', label: 'Data Announcements', shortLabel: 'Data' },
  { name: 'Product Launch', label: 'Product Launches', shortLabel: 'Product Launch' },
  { name: UNCLASSIFIED_CATEGORY, label: 'Unclassified', shortLabel: 'Unclassified' }
];

const CATEGORY_BY_NAME = new Map<string, HeadlineCategory>(
  HEADLINE_CATEGORIES.map(category => [category.name, category])
);

/**
 * Resolves a classifier string to its catalog entry. Unknown strings (e.g. a new
 * classifier value) fall back to showing themselves verbatim, so nothing crashes
 * and unexpected categories stay visible rather than silently disappearing. That
 * includes `shortLabel`: there is no abbreviation to guess, and rendering the value
 * long is better than rendering it blank.
 */
export function getHeadlineCategory(name: string): HeadlineCategory {
  return CATEGORY_BY_NAME.get(name) ?? { name, label: name, shortLabel: name };
}
